import { Classes as BlueprintClasses } from '@blueprintjs/core';
import { ChevronDown, ChevronUp } from '@carbon/icons-react';
import classNames from 'classnames';
import * as React from 'react';

import { useVoiceboxSchema } from 'bones/components/molecules/Voicebox/context/VoiceboxSchema';
import { useVoiceboxSettings } from 'bones/components/molecules/Voicebox/context/VoiceboxSettings';
import { DEFAULT_MODEL_NAME } from 'bones/constants/model';
import { Button } from 'bones/design-system/components/Button';
import { Heading } from 'bones/design-system/components/typography/Heading';

const SEKELETON_LINES_LENGTH = 3;

export interface VoiceboxDescriptionProps {
  databaseId: string;
  model: string;
  size: 'lg' | 'sm';
}

export const VoiceboxDescription: React.VFC<VoiceboxDescriptionProps> = ({
  databaseId,
  model,
  size,
}) => {
  const { config, copy, testIds } = useVoiceboxSchema();
  const { descriptionsByDatabaseModel, descriptionsLoading } =
    useVoiceboxSettings();

  const [isExpanded, setIsExpanded] = React.useState(false);
  const [isTruncated, setIsTruncated] = React.useState(false);

  const description =
    config.description ||
    descriptionsByDatabaseModel[databaseId]?.[model || DEFAULT_MODEL_NAME];

  const descriptionRef = React.useRef<HTMLDivElement>(null);
  React.useLayoutEffect(() => {
    const descriptionDiv = descriptionRef.current;
    if (descriptionDiv) {
      if (
        descriptionDiv.offsetHeight &&
        descriptionDiv.scrollHeight &&
        descriptionDiv.offsetHeight < descriptionDiv.scrollHeight
      ) {
        setIsTruncated(true);
      } else {
        setIsTruncated(false);
      }
    }
  }, [description, descriptionsLoading]);

  const handleExpand = (nextIsExpanded: boolean) => {
    if (!nextIsExpanded && descriptionRef.current) {
      descriptionRef.current.scrollTop = 0;
    }
    setIsExpanded(nextIsExpanded);
  };

  let body: JSX.Element | string = '';
  if (description) {
    body = (
      <>
        <Heading divRef={descriptionRef} size={size} weight="light">
          {description}
        </Heading>
        {isTruncated ? (
          <Button
            leftIcon={isExpanded ? ChevronUp : ChevronDown}
            onClick={() => handleExpand(!isExpanded)}
            size="xs"
            testId={
              isExpanded
                ? testIds.messages.description.showLess
                : testIds.messages.description.showMore
            }
            variant="ghost-gray"
          >
            {isExpanded
              ? copy.message.description.showLess
              : copy.message.description.showMore}
          </Button>
        ) : null}
      </>
    );
  } else if (descriptionsLoading) {
    body = (
      <div
        className="vb-voicebox-description-loading"
        data-testid={testIds.messages.description.loading}
      >
        {new Array(SEKELETON_LINES_LENGTH).fill('').map((_, idx) => (
          // eslint-disable-next-line react/no-array-index-key
          <div key={idx} className={BlueprintClasses.SKELETON} />
        ))}
      </div>
    );
  }

  if (!body) {
    return null;
  }

  return (
    <div className="vb-voicebox-description">
      <div
        className={classNames('vb-voicebox-description-transition', {
          'vb-voicebox-description-transition--collapsed': !isExpanded,
        })}
        data-testid={testIds.messages.description.text}
      >
        {body}
      </div>
    </div>
  );
};
